import React, { useState, useEffect } from "react";
import {
  fetchWithAuth,
  fetchWithAuthUpload,
} from "../../../Context/UserContext";
import { useParams, useNavigate } from "react-router-dom";
import PageLayout from "../../../components/layout/PageLayout";
import { LoadingOverlay } from "../../../components/ui/loading-overlay";
import API_URL from "../../../config";
import { toast } from "sonner";
import { Card, CardContent } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { FileText } from "lucide-react";

const starColors = ["#d62246", "#f97316", "#eab308", "#4bbf73", "#2A9D8F"];

const StarRating = ({ hoveredStars, selectedStars, onHover, onLeave, onClick }) => (
  <div className="flex">
    {Array(5)
      .fill(0)
      .map((_, index) => (
        <button
          key={index}
          type="button"
          className="flex h-7 w-7 items-center justify-center rounded-full text-sm"
          style={{
            backgroundColor:
              hoveredStars >= index + 1 || selectedStars >= index + 1
                ? starColors[selectedStars || hoveredStars - 1] || "#ccc"
                : "#ccc",
            color: "#fff",
          }}
          onMouseEnter={() => onHover(index + 1)}
          onMouseLeave={onLeave}
          onClick={() => onClick(index + 1)}
        >
          ★
        </button>
      ))}
  </div>
);

const GanadorLicitacion = () => {
  const { contractId } = useParams(); // Obtener el ID del contrato desde la URL
  const navigate = useNavigate();
  const [contractInfo, setContractInfo] = useState(null);
  const [hoveredStars, setHoveredStars] = useState(0);
  const [selectedStars, setSelectedStars] = useState(0);
  const [showTenderDetails, setShowTenderDetails] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    // Obtener información del contrato
    const fetchContractInfo = async () => {
      try {
        const response = await fetchWithAuth(
          `${API_URL}/licit/contract/${contractId}`
        );
        if (response.ok) {
          const data = await response.json();
          setContractInfo(data);
        } else {
          console.error("Error al obtener la información del contrato.");
        }
      } catch (error) {
        console.error("Error al obtener la información del contrato:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchContractInfo();
  }, [contractId]);

  const handleStarClick = (score) => {
    setIsLoading(true);
    setSelectedStars(score);

    // Datos que queremos pasar según el rol
    const reviewTarget =
      contractInfo.role === "licitador"
        ? contractInfo.ganador.nombre_empresa
          ? {
              id_tender: contractInfo.tender.id_tender,
              id_empresa: contractInfo.ganador.id_empresa,
              nombre_empresa: contractInfo.ganador.nombre_empresa,
            }
          : {
              id_tender: contractInfo.tender.id_tender,
              id_user: contractInfo.ganador.id_user,
              nombre: contractInfo.ganador.nombre,
              apellido: contractInfo.ganador.apellido,
            }
        : contractInfo.licitador.nombre_empresa
        ? {
            id_tender: contractInfo.tender.id_tender,
            id_empresa: contractInfo.licitador.id_empresa,
            nombre_empresa: contractInfo.licitador.nombre_empresa,
          }
        : {
            id_tender: contractInfo.tender.id_tender,
            id_user: contractInfo.licitador.id_user,
            nombre: contractInfo.licitador.nombre,
            apellido: contractInfo.licitador.apellido,
          };

    // Navega a Reseñas pasando el objeto
    setIsLoading(false);

    navigate("/reseñas", { state: { score, reviewTarget } });
  };

  const downloadFile = async (fileId) => {
    setIsLoading(true);
    try {
      console.log(`⬇️ Solicitando descarga del archivo con ID: ${fileId}`);

      const response = await fetchWithAuthUpload(
        `${API_URL}/upload/download-file/${fileId}`,
        { method: "GET" }
      );

      if (!response.ok) {
        throw new Error("Error al obtener el archivo");
      }

      const data = await response.json();
      console.log("📡 Respuesta del backend:", data);

      const fileUrl = data.file_url; // ✅ URL presignada de S3
      console.log(`📂 Abriendo URL en nueva pestaña: ${fileUrl}`);

      // ✅ Abrir la URL presignada directamente (deja el UUID en el nombre)
      window.open(fileUrl, "_blank");
    } catch (error) {
      console.error("❌ Error al descargar archivo:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveParticipant = async () => {
    setIsLoading(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/licit/tender/${contractInfo.tender.id_tender}/remove-winner`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            applicationId: contractId,
          }),
        }
      );

      if (response.ok) {
        toast.success("Acción completada con éxito.");
        // Redirigir según el rol del usuario
        if (contractInfo.role === "licitador") {
          // Si es el dueño de la licitación, va a ver las postulaciones
          window.location.href = `/ver-postulaciones/${contractInfo.tender.id_tender}`;
        } else if (contractInfo.role === "ganador") {
          // Si es el postulador ganador que se retira, va a sus postulaciones
          window.location.href = `/mis-postulaciones`;
        }
      } else {
        const error = await response.json();
        toast.error(`Error: ${error.error}`);
      }
    } catch (error) {
      console.error("Error al realizar la acción:", error);
      toast.error("Ocurrió un error al intentar completar la acción.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <PageLayout>
      {isLoading ? (
        <LoadingOverlay />
      ) : !contractInfo || !contractInfo.ganador || !contractInfo.licitador ? (
        <p className="px-4 py-16 text-center text-destructive">Error: No se pudo cargar la información.</p>
      ) : (
        <div className="mx-auto max-w-4xl space-y-4 px-4 py-8 sm:px-6">
          <h3 className="text-xl font-semibold text-foreground">
            Administrar Licitación: {contractInfo.tender.titulo}
          </h3>

          {/* Sección de información de la licitación */}
          <Card>
            <CardContent className="space-y-2 pt-6">
              <p className="text-sm text-muted-foreground">
                <strong className="text-foreground">Nº de Licitación:</strong> {contractInfo.tender.id_tender}
              </p>
              <p className="text-sm text-muted-foreground">
                <strong className="text-foreground">Categoría:</strong> {contractInfo.tender.categoria}
              </p>
              <p className="text-sm text-muted-foreground">
                <strong className="text-foreground">Ciudades del servicio:</strong>{" "}
                {(() => {
                  try {
                    const ciudades = JSON.parse(contractInfo.tender.ciudad);
                    if (Array.isArray(ciudades)) {
                      const visibleCiudades = ciudades.slice(0, 6); // Limitar a 4 ciudades
                      return ciudades.length > 6
                        ? `${visibleCiudades.join(", ")}...` // Mostrar solo las primeras 4 con "..." si hay más
                        : visibleCiudades.join(", ");
                    }
                    return "No especificado";
                  } catch (e) {
                    return "No especificado";
                  }
                })()}
              </p>
              <p className="text-sm text-muted-foreground">
                <strong className="text-foreground">Actualizado:</strong>{" "}
                {new Date(contractInfo.tender.updated_at).toLocaleString("es-ES", {
                  weekday: "long",
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </p>
              <Button variant="outline" size="sm" onClick={() => setShowTenderDetails(!showTenderDetails)}>
                {showTenderDetails ? "Ocultar detalles del tender" : "Mostrar detalles del tender"}
              </Button>
            </CardContent>
          </Card>

          {showTenderDetails && (
            <Card>
              <CardContent className="space-y-2 pt-6">
                <h3 className="font-semibold text-foreground">Detalles de la Licitación</h3>
                <p className="text-sm text-muted-foreground">
                  <strong className="text-foreground">Descripción:</strong>{" "}
                  {contractInfo.tender.descripcion || "No especificada"}
                </p>
                <p className="text-sm text-muted-foreground">
                  <strong className="text-foreground">Condiciones de Pago:</strong>{" "}
                  {(() => {
                    try {
                      const condicionesPago = JSON.parse(contractInfo.tender.condiciones_pago);
                      return Array.isArray(condicionesPago) ? condicionesPago.join(", ") : "No especificadas";
                    } catch {
                      return "No especificadas";
                    }
                  })()}
                </p>
                <p className="text-sm text-muted-foreground">
                  <strong className="text-foreground">Duración del Contrato:</strong>{" "}
                  {(() => {
                    try {
                      const duracionContrato = JSON.parse(contractInfo.tender.duracion_contrato);
                      return Array.isArray(duracionContrato) ? duracionContrato.join(", ") : "No especificada";
                    } catch {
                      return "No especificada";
                    }
                  })()}
                </p>

                {contractInfo.tender.archivos_adjuntos.length > 0 && (
                  <div>
                    <p className="text-sm text-muted-foreground">
                      📂 <strong className="text-foreground">Archivos adjuntos de la licitación:</strong>
                    </p>
                    <ul className="mt-1 space-y-1">
                      {contractInfo.tender.archivos_adjuntos.map((archivo, index) => (
                        <li key={index}>
                          <button
                            onClick={() => downloadFile(archivo.id_file)}
                            className="flex items-center gap-1.5 text-sm text-primary hover:underline"
                          >
                            <FileText className="h-4 w-4" /> {archivo.file_name}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            {/* Información del licitador */}
            <Card>
              <CardContent className="space-y-2 pt-6">
                <h3 className="font-semibold text-foreground">Creador de la licitación</h3>
                <p className="text-sm text-muted-foreground">
                  <strong className="text-foreground">Nombre:</strong>{" "}
                  <a
                    href={`/profile/${contractInfo.licitador.id_user}/${encodeURIComponent(
                      `${contractInfo.licitador.nombre} ${contractInfo.licitador.apellido}`
                    )}`}
                    className="text-primary hover:underline"
                  >
                    {contractInfo.licitador.nombre} {contractInfo.licitador.apellido}
                  </a>
                </p>
                <p className="text-sm text-muted-foreground">
                  <strong className="text-foreground">Empresa:</strong>{" "}
                  {contractInfo.licitador.nombre_empresa ? (
                    <a
                      href={`/company-profile/${contractInfo.licitador.id_empresa}/${encodeURIComponent(
                        contractInfo.licitador.nombre_empresa
                      )}`}
                      className="text-primary hover:underline"
                    >
                      {contractInfo.licitador.nombre_empresa}
                    </a>
                  ) : (
                    "N/A"
                  )}
                </p>
                <p className="text-sm text-muted-foreground">
                  <strong className="text-foreground">Correo:</strong> {contractInfo.licitador.correo}
                </p>

                {contractInfo.role === "ganador" && (
                  <>
                    <p className="text-sm text-muted-foreground">
                      <strong className="text-foreground">Evalúa al licitador:</strong>
                    </p>
                    <StarRating
                      hoveredStars={hoveredStars}
                      selectedStars={selectedStars}
                      onHover={setHoveredStars}
                      onLeave={() => setHoveredStars(0)}
                      onClick={handleStarClick}
                    />
                  </>
                )}
              </CardContent>
            </Card>

            {/* Información del ganador */}
            <Card>
              <CardContent className="space-y-2 pt-6">
                <h3 className="font-semibold text-foreground">Ganador de la licitación</h3>
                <p className="text-sm text-muted-foreground">
                  <strong className="text-foreground">Nombre:</strong>{" "}
                  {contractInfo.ganador ? (
                    <a
                      href={`/profile/${contractInfo.ganador.id_user || ""}/${encodeURIComponent(
                        `${contractInfo.ganador.nombre || ""} ${contractInfo.ganador.apellido || ""}`
                      )}`}
                      className="text-primary hover:underline"
                    >
                      {contractInfo.ganador.nombre} {contractInfo.ganador.apellido}
                    </a>
                  ) : (
                    "Información no disponible"
                  )}
                </p>
                <p className="text-sm text-muted-foreground">
                  <strong className="text-foreground">Empresa:</strong>{" "}
                  {contractInfo.ganador.nombre_empresa ? (
                    <a
                      href={`/company-profile/${contractInfo.ganador.id_empresa}/${encodeURIComponent(
                        contractInfo.ganador.nombre_empresa
                      )}`}
                      className="text-primary hover:underline"
                    >
                      {contractInfo.ganador.nombre_empresa}
                    </a>
                  ) : (
                    "N/A"
                  )}
                </p>
                <p className="text-sm text-muted-foreground">
                  <strong className="text-foreground">Correo:</strong> {contractInfo.ganador.correo}
                </p>

                {contractInfo.role === "licitador" && (
                  <>
                    <p className="text-sm text-muted-foreground">
                      <strong className="text-foreground">Evalúa al proveedor:</strong>
                    </p>
                    <StarRating
                      hoveredStars={hoveredStars}
                      selectedStars={selectedStars}
                      onHover={setHoveredStars}
                      onLeave={() => setHoveredStars(0)}
                      onClick={handleStarClick}
                    />
                  </>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Información de la aplicación ganadora */}
          <Card>
            <CardContent className="space-y-2 pt-6">
              <h3 className="font-semibold text-foreground">Detalles de la Postulación Ganadora</h3>
              <p className="text-sm text-muted-foreground">
                <strong className="text-foreground">Oferta presentada:</strong>{" "}
                {contractInfo.ganador.detalles_oferta || "No especificado"}
              </p>
              {contractInfo.ganador.archivos_adjuntos_postulante.length > 0 && (
                <div>
                  <p className="text-sm text-muted-foreground">
                    📂<strong className="text-foreground">Archivos adjuntos</strong> del postulante:
                  </p>
                  <ul className="mt-1 space-y-1">
                    {contractInfo.ganador.archivos_adjuntos_postulante.map((archivo, index) => (
                      <li key={index}>
                        <button
                          onClick={() => downloadFile(archivo.id_file)}
                          className="flex items-center gap-1.5 text-sm text-primary hover:underline"
                        >
                          <FileText className="h-4 w-4" /> {archivo.file_name}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              {contractInfo.ganador.respuestas_preguntas_licitador &&
              Object.keys(contractInfo.ganador.respuestas_preguntas_licitador).length > 0 ? (
                <div>
                  <h3 className="font-semibold text-foreground">Respuestas del ganador a las preguntas del licitador</h3>
                  <ul className="mt-2 space-y-3">
                    {Object.entries(contractInfo.ganador.respuestas_preguntas_licitador).map(
                      ([pregunta, respuesta], index) => {
                        const archivosPregunta = contractInfo.ganador.archivos_adjuntos_pregunta.filter(
                          (archivo) => archivo.id_pregunta === pregunta
                        );

                        return (
                          <li key={index} className="text-sm">
                            <strong className="text-foreground">
                              {index + 1}. {pregunta}
                            </strong>
                            {respuesta && (
                              <p className="mt-0.5 text-muted-foreground">
                                <strong>-</strong> {respuesta}
                              </p>
                            )}
                            {archivosPregunta.length > 0 && (
                              <ul className="mt-1 space-y-1">
                                {archivosPregunta.map((archivo, idx) => (
                                  <li key={idx}>
                                    <button
                                      onClick={() => downloadFile(archivo.id_file)}
                                      className="flex items-center gap-1.5 text-primary hover:underline"
                                    >
                                      <FileText className="h-4 w-4" /> {archivo.file_name}
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </li>
                        );
                      }
                    )}
                  </ul>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No hay respuestas registradas.</p>
              )}
            </CardContent>
          </Card>

          {/* Botones de accion*/}
          <Card>
            <CardContent className="space-y-2 pt-6">
              <p className="text-sm text-muted-foreground">
                <strong className="text-foreground">
                  {contractInfo.role === "licitador"
                    ? "Elimina al ganador que escogiste:"
                    : "Elimina tu postulación a esta licitación:"}
                </strong>
              </p>
              <Button variant="destructive" onClick={handleRemoveParticipant}>
                Eliminar
              </Button>
            </CardContent>
          </Card>
        </div>
      )}
    </PageLayout>
  );
};

export default GanadorLicitacion;
